import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { observabilityApi } from '../../../services/api';
import { Check } from 'lucide-react';

const AnomaliesTab: React.FC<{ projectId?: number }> = ({ projectId }) => {
  const [sev, setSev] = useState<string | undefined>(undefined);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['obs-anomalies', sev, projectId ?? 'all'],
    queryFn: async () => (await observabilityApi.listAnomalies({
      ...(sev ? { severity: sev } : {}),
      ...(projectId ? { project_id: projectId } : {}),
    })).data.data,
    refetchInterval: 60000,
  });

  const ack = async (id: number) => {
    await observabilityApi.ackAnomaly(id);
    qc.invalidateQueries({ queryKey: ['obs-anomalies'] });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <FilterBtn label="All" active={!sev} onClick={() => setSev(undefined)} />
        <FilterBtn label="High" active={sev === 'high'} onClick={() => setSev('high')} />
        <FilterBtn label="Medium" active={sev === 'medium'} onClick={() => setSev('medium')} />
        <FilterBtn label="Low" active={sev === 'low'} onClick={() => setSev('low')} />
        <div className="ml-auto text-xs text-cargo-muted">Live feed — refreshes every 5s</div>
      </div>

      <div className="bg-white rounded-xl border border-cargo-border overflow-hidden">
        {q.isLoading ? (
          <div className="p-8 text-center text-sm text-cargo-muted">Loading...</div>
        ) : (q.data || []).length === 0 ? (
          <div className="p-10 text-center text-cargo-muted text-sm">🟢 No anomalies — everything looks normal.</div>
        ) : (
          <ul>
            {(q.data || []).map((a: any) => (
              <li key={a.id} className="flex items-start gap-3 p-4 border-b border-cargo-border last:border-b-0 hover:bg-gray-50">
                <SevBadge sev={a.severity} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">
                    {a.table_name}
                    {a.column_name && <span className="text-cargo-muted">.{a.column_name}</span>}
                    <span className="ml-2 text-xs text-cargo-muted font-normal">
                      {a.pillar} / {a.metric}
                    </span>
                  </div>
                  <div className="text-sm text-cargo-muted mt-0.5">{a.message}</div>
                  <div className="text-xs text-cargo-muted mt-1">
                    {a.detected_at}
                    {a.z_score != null && <span className="ml-3">z-score: {a.z_score.toFixed(2)}</span>}
                    {a.incident_id && <span className="ml-3">→ Incident #{a.incident_id}</span>}
                  </div>
                </div>
                {!a.acknowledged && (
                  <button onClick={() => ack(a.id)} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg border border-cargo-border hover:bg-gray-100">
                    <Check className="w-3 h-3" /> Ack
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const FilterBtn: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
  <button onClick={onClick}
    className={`text-xs px-3 py-1.5 rounded-lg border ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-cargo-text border-cargo-border hover:bg-gray-50'}`}>
    {label}
  </button>
);

const SevBadge: React.FC<{ sev: string }> = ({ sev }) => {
  const color = sev === 'high' ? 'bg-red-100 text-red-700' : sev === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700';
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${color} mt-0.5`}>{sev}</span>;
};

export default AnomaliesTab;
